/**
 * Sandbox **runtime/store** helpers: requests, certificates, IOCs, geo, tag rendering, `flattenSignalGroups`, …
 * For **`full_report` / `resources` JSON shaping** (`groupResourcesByCategory`, `mergeFullReportRootIntoGroupedResources`), see `sandboxReport.ts`.
 */
import dayjs from 'dayjs';
import type {
    Certificate,
    GeoData,
    ParsedReport,
    PhishDetection,
    RenderResult,
    Report,
    ReportData,
    Request,
    Resource,
    SandboxData,
    SandboxResources,
    SandboxResponseInterface,
    SandboxResultItem,
    SandboxSignalGroup,
} from '../services/interface';
import { mapTagsSandboxToScanStatus } from 'utils/Verdict';
import { Tag } from '@opswat/ui';

/** SWR `refreshInterval` callback while sandbox metadata is in flight (file + URL flows). */
export const sandboxRefreshInterval = (data?: SandboxResponseInterface) => {
    if (data?.scan_in_progress) {
        return 10000;
    }

    if (
        data?.final_verdict ||
        data?.scan_results?.progress_percentage === 100 ||
        data?.scan_results?.scan_all_result_a === 'Aborted'
    ) {
        return 0;
    }

    return 3000;
};

/** Reads verdict from URL `signal_groups` (string) or file reports (nested object). */
export const getSandboxSignalGroupVerdictString = (group: { verdict?: unknown }): string => {
    const v = group?.verdict;
    if (typeof v === 'string') {
        return v;
    }
    if (v && typeof v === 'object' && v !== null && 'verdict' in v) {
        const inner = (v as { verdict?: unknown }).verdict;
        if (typeof inner === 'string') {
            return inner;
        }
    }
    return 'Unknown';
};

export const getResources = (data: ReportData): Resource[] => {
    if (data?.reports && typeof data.reports === 'object') {
        const reportList = Object.values(data.reports);
        const resourceList = reportList.reduce((acc: Resource[], report: Report) => {
            if (report?.resources) {
                return [...acc, ...Object.values(report.resources)];
            }
            return acc;
        }, []);
        return resourceList;
    } else if (data?.full_report) {
        try {
            const parsedReport = JSON.parse(data.full_report) as ParsedReport;
            if (parsedReport?.resources && typeof parsedReport.resources === 'object') {
                return Object.values(parsedReport.resources);
            }
        } catch {
            return [];
        }
    }
    return [];
};

/** Both `{ renderResults: RenderResult[] }` and a bare `RenderResult[]` appear in sandbox store JSON. */
const getRenderResultsFromResource = (resource: Resource): RenderResult[] => {
    const rr = resource?.renderResults;
    if (Array.isArray(rr)) {
        return rr;
    }
    if (rr && typeof rr === 'object' && Array.isArray((rr as { renderResults?: RenderResult[] }).renderResults)) {
        return (rr as { renderResults: RenderResult[] }).renderResults;
    }
    return [];
};

export const getRenderResults = (data: ReportData): RenderResult[] => {
    const resourceList = getResources(data);
    return resourceList.flatMap((resource) => getRenderResultsFromResource(resource));
};

export const getCertificates = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const certificateList = renderResultsList.reduce((acc: Certificate[], renderResults: RenderResult) => {
        const certificates = renderResults?.urlRenderData?.result?.lists?.certificates;
        if (Array.isArray(certificates)) {
            return [...acc, ...certificates];
        }
        return acc;
    }, []);
    const certificateData = certificateList.map((certificate: Certificate) => {
        const validFrom = dayjs(certificate.validFrom * 1000).format('D MMM YYYY HH:mm');
        const validTo = dayjs(certificate.validTo * 1000).format('D MMM YYYY HH:mm');
        return {
            subjectName: certificate.subjectName,
            issuer: certificate.issuer,
            validFrom,
            validTo,
        };
    });
    return certificateData;
};

export const getRequests = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const requestList = renderResultsList.reduce((acc: Request[], renderResults: RenderResult) => {
        const requests = renderResults?.urlRenderData?.result?.data?.requests;

        if (requests && typeof requests === 'object') {
            return [...acc, ...Object.values(requests)];
        }

        return acc;
    }, []);

    return requestList;
};

export const getPhishDetections = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const phishDetectionList = renderResultsList.reduce((acc: PhishDetection[], renderResults: RenderResult) => {
        const phishDetection = renderResults?.urlRenderData?.result?.phishDetection;
        const phishDetectionState = renderResults?.urlRenderData?.phishDetectionState;

        if (phishDetection && Object.keys(phishDetection).length > 0 && typeof phishDetectionState === 'string') {
            return [...acc, { ...phishDetection, phishDetectionState }];
        }

        return [...acc];
    }, []);

    return phishDetectionList;
};

export const getImages = (data: ReportData) => {
    const renderResultsList = getRenderResults(data);
    const imageList = renderResultsList.reduce((acc: string[], renderResults: RenderResult) => {
        const base64 = renderResults?.base64;

        if (base64) {
            return [...acc, base64];
        }

        return [...acc];
    }, []);

    return imageList;
};

export const flattenSignalGroups = (sandboxData?: SandboxData): SandboxSignalGroup[] => {
    const reports = sandboxData?.reports;
    const fullReport = sandboxData?.full_report;

    if (!reports && !fullReport) {
        return [];
    }

    const allSignalGroups: SandboxSignalGroup[] = [];

    if (reports) {
        Object.values(reports).forEach((report) => {
            if (report.resources) {
                Object.values(report.resources).forEach((resource) => {
                    if (resource.signalGroups && Array.isArray(resource.signalGroups)) {
                        allSignalGroups.push(...resource.signalGroups);
                    }
                });
            }
        });
    }

    if (fullReport) {
        const parsedFullReport: unknown = typeof fullReport === 'string' ? JSON.parse(fullReport) : fullReport;

        const maybeAllSignalGroups =
            typeof parsedFullReport === 'object' && parsedFullReport !== null
                ? Reflect.get(parsedFullReport, 'allSignalGroups')
                : undefined;

        const extracted = Array.isArray(maybeAllSignalGroups) ? (maybeAllSignalGroups as SandboxSignalGroup[]) : [];

        allSignalGroups.push(...extracted);

        const maybeSnake =
            typeof parsedFullReport === 'object' && parsedFullReport !== null
                ? Reflect.get(parsedFullReport, 'signal_groups')
                : undefined;

        if (Array.isArray(maybeSnake) && maybeSnake.length > 0) {
            allSignalGroups.push(...(maybeSnake as SandboxSignalGroup[]));
        }
    }

    return allSignalGroups.filter(Boolean);
};

export const getTotalThreatIndicatorsFromResources = (sandboxData?: SandboxData): number => {
    return flattenSignalGroups(sandboxData).length;
};

const iocTypeMap: Record<string, string> = {
    url: 'URL',
    ip: 'IP',
    domain: 'Domain',
    email: 'Email',
    md5: 'MD5',
    sha1: 'SHA1',
    sha256: 'SHA-256',
    sha512: 'SHA-512',
    uuid: 'UUID',
    registry_path: 'Registry Pathway',
    revision_save_id: 'Revision Save ID',
    btc_wallet: 'BTC Wallet',
    eth_wallet: 'ETH Wallet',
    xmr_wallet: 'XMR Wallet',
    unc_path: 'UNC Path',
    uncategorized: 'Uncategorized',
};

const formatFallbackIocType = (typeKey: string) =>
    typeKey
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

export const getIocType = (typeKey: string) => iocTypeMap[typeKey] || formatFallbackIocType(typeKey);

export const getIocValue = (item: unknown): string => {
    if (typeof item === 'string') {
        return item;
    }
    if (!isRecord(item)) {
        return '';
    }
    const possibleValue = item.value ?? item.data ?? item.ioc ?? item.indicator ?? item.str ?? item.hash;
    return typeof possibleValue === 'string' ? possibleValue : '';
};

export const getGroupedIocsCount = (sandboxReport?: SandboxData): number => {
    if (!sandboxReport?.full_report) {
        return 0;
    }
    let parsedReport: unknown;
    if (typeof sandboxReport.full_report === 'string') {
        try {
            parsedReport = JSON.parse(sandboxReport.full_report);
        } catch {
            return 0;
        }
    } else {
        parsedReport = sandboxReport.full_report;
    }
    if (!isRecord(parsedReport) || !isRecord(parsedReport.iocs)) {
        return 0;
    }
    const keys = new Set<string>();
    for (const [typeKey, typeValues] of Object.entries(parsedReport.iocs)) {
        if (!Array.isArray(typeValues)) {
            continue;
        }
        for (const item of typeValues) {
            const value = getIocValue(item);
            if (!value) {
                continue;
            }
            const type = getIocType(typeKey);
            keys.add(`${type}||${value}`);
        }
    }
    return keys.size;
};

export const getTotalYaraRulesFromResources = (sandboxResources: SandboxResources): number => {
    if (!sandboxResources?.yaraMatches) {
        return 0;
    }
    return sandboxResources.yaraMatches.reduce(
        (total: number, group) => total + (Array.isArray(group) ? group.length : 0),
        0,
    );
};

export const getTotalEmulationEventsFromResources = (sandboxResources: SandboxResources): number => {
    if (!sandboxResources?.emulationData) {
        return 0;
    }
    const emulationData = sandboxResources.emulationData;
    if (Array.isArray(emulationData) && emulationData.length > 0 && !Array.isArray(emulationData[0])) {
        return emulationData.length;
    }
    if (Array.isArray(emulationData) && emulationData.length > 0 && Array.isArray(emulationData[0])) {
        return (emulationData as unknown[][]).reduce(
            (total: number, group) => total + (Array.isArray(group) ? group.length : 0),
            0,
        );
    }
    return 0;
};

export const getTotalExtractedFilesFromResources = (sandboxResources: SandboxResources): number => {
    let total = 0;
    if (sandboxResources?.extractedFiles) {
        total += sandboxResources.extractedFiles.reduce(
            (count: number, group) => count + (Array.isArray(group) ? group.length : 0),
            0,
        );
    }
    if (sandboxResources?.fileDownloadResults) {
        total += sandboxResources.fileDownloadResults.reduce(
            (count: number, group) => count + (Array.isArray(group) ? group.length : 0),
            0,
        );
    }

    return total;
};

export const getTotalExtractedStringsFromResources = (sandboxResources: SandboxResources): number => {
    if (!sandboxResources) {
        return 0;
    }

    if (!Array.isArray(sandboxResources.strings)) {
        return 0;
    }

    return sandboxResources.strings.reduce((total: number, group) => {
        if (!Array.isArray(group) || group.length === 0) {
            return total;
        }

        const first = group[0];

        if (first && typeof first === 'object' && 'references' in first) {
            const groupTotal = group.reduce((acc, item) => {
                if (item && typeof item === 'object' && 'references' in item) {
                    const maybeReferences = (item as { references?: unknown }).references;
                    if (Array.isArray(maybeReferences)) {
                        return acc + maybeReferences.length;
                    }
                }
                return acc;
            }, 0);

            return total + groupTotal;
        }

        return total + group.length;
    }, 0);
};

export const getGeoDataFromResources = (sandboxResources: SandboxResources): GeoData[] => {
    const results = Array.isArray(sandboxResources?.results) ? sandboxResources.results : [];

    const hasGeoInfo = (
        item: SandboxResultItem,
    ): item is SandboxResultItem & {
        data: {
            geo_info: {
                country: { name: string };
                location: { latitude: number; longitude: number };
            };
        };
    } => {
        const name = item?.data?.geo_info?.country?.name;
        const latitude = item?.data?.geo_info?.location?.latitude;
        const longitude = item?.data?.geo_info?.location?.longitude;
        return typeof name === 'string' && typeof latitude === 'number' && typeof longitude === 'number';
    };

    return results
        .flat()
        .filter(hasGeoInfo)
        .map((item) => {
            const name = item.data.geo_info.country.name;
            const latitude = item.data.geo_info.location.latitude;
            const longitude = item.data.geo_info.location.longitude;
            return {
                country: { name },
                location: { latitude, longitude },
            };
        });
};

type SandboxTagVerdict = { verdict?: string };
type SandboxTag = { name: string; verdict?: SandboxTagVerdict };

export const getSandboxTagsFromResources = (sandboxResources: SandboxResources): SandboxTag[] => {
    if (!sandboxResources) {
        return [];
    }

    return Array.from(
        (Array.isArray(sandboxResources?.allTags) ? sandboxResources.allTags : [])
            .flat()
            .map((tagEntry: unknown) => {
                if (tagEntry && typeof tagEntry === 'object' && 'tag' in tagEntry) {
                    return (tagEntry as { tag?: unknown }).tag ?? tagEntry;
                }
                return tagEntry;
            })
            .filter((tag: unknown): tag is SandboxTag => {
                return (
                    !!tag &&
                    typeof tag === 'object' &&
                    'name' in tag &&
                    typeof (tag as { name?: unknown }).name === 'string'
                );
            })
            .reduce((map: Map<string, SandboxTag>, tag: SandboxTag) => {
                if (!map.has(tag.name)) {
                    map.set(tag.name, { name: tag.name, verdict: (tag as { verdict?: SandboxTagVerdict }).verdict });
                }
                return map;
            }, new Map<string, SandboxTag>())
            .values(),
    );
};

export interface IoCData {
    type: string;
    name: string;
    value: string;
    properties?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
}

export const renderSandboxTags = (sandboxTags: SandboxTag[], limit?: number) => {
    const priority = ['MALICIOUS', 'LIKELY_MALICIOUS', 'SUSPICIOUS'];

    const sortedTags = [...sandboxTags].sort((firstTag, secondTag) => {
        const verdictA = firstTag?.verdict?.verdict ?? '';
        const verdictB = secondTag?.verdict?.verdict ?? '';

        const indexA = priority.indexOf(verdictA);
        const indexB = priority.indexOf(verdictB);

        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        } else if (indexA !== -1) {
            return -1;
        } else if (indexB !== -1) {
            return 1;
        }
        return 0;
    });

    const tagsToShow = limit ? sortedTags.slice(0, limit) : sortedTags;

    return (
        <div className='tw:flex tw:flex-row tw:flex-wrap tw:gap-8'>
            {tagsToShow.map((tag, index) => {
                const verdict = tag?.verdict?.verdict;
                if (!verdict) return null;

                const status = mapTagsSandboxToScanStatus(verdict);
                return (
                    <div key={index} className='tw:flex tw:items-center tw:gap-8'>
                        <Tag variant={status.variant} text={tag?.name} className='tw:w-fit' type='keyword' />
                    </div>
                );
            })}
        </div>
    );
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

/** Normalize sandbox IOC `origin` objects (shape varies by API version). */
const originRecordToDisplayString = (origin: unknown): string => {
    if (typeof origin === 'string') {
        return origin;
    }
    if (!isRecord(origin)) {
        return '';
    }
    if (typeof origin.type === 'string') {
        return origin.type;
    }
    if (typeof origin.name === 'string') {
        return origin.name;
    }
    if (typeof origin.source === 'string') {
        return origin.source;
    }
    if (typeof origin.label === 'string') {
        return origin.label;
    }
    if (typeof origin.identifier === 'string' && origin.identifier.length > 0) {
        return origin.identifier;
    }
    return '';
};

const originsArrayToDisplayString = (origins: unknown): string => {
    if (!Array.isArray(origins) || origins.length === 0) {
        return '';
    }
    const parts = origins
        .map((entry) => originRecordToDisplayString(entry))
        .filter((part): part is string => part.length > 0);
    const seen = new Set<string>();
    const unique = parts.filter((part) => {
        if (seen.has(part)) {
            return false;
        }
        seen.add(part);
        return true;
    });
    return unique.join(', ');
};

export const getIocOrigin = (item: unknown): string => {
    if (!isRecord(item)) {
        return '';
    }

    const fromTopLevelOrigin = originRecordToDisplayString(item.origin);
    if (fromTopLevelOrigin) {
        return fromTopLevelOrigin;
    }

    const fromOriginsArray = originsArrayToDisplayString(item.origins);
    if (fromOriginsArray) {
        return fromOriginsArray;
    }

    if (typeof item.source === 'string') {
        return item.source;
    }

    if (typeof item.sourceIdentifier === 'string') {
        return item.sourceIdentifier;
    }

    if (isRecord(item.metadata)) {
        const meta = item.metadata;
        const fromMetaOrigin = originRecordToDisplayString(meta.origin);
        if (fromMetaOrigin) {
            return fromMetaOrigin;
        }
        if (typeof meta.source === 'string') {
            return meta.source;
        }
    }

    return '';
};

export const getIocVerdict = (item: unknown): string => {
    if (!isRecord(item)) {
        return '';
    }

    if (typeof item.verdict === 'string') {
        return item.verdict;
    }

    if (isRecord(item.metadata) && typeof item.metadata.verdict === 'string') {
        return item.metadata.verdict;
    }

    return '';
};

export interface SandboxFullReportExportOption {
    key: string;
    label: string;
    icon: string;
    url: string;
}

/** `full_report` link object from `/v4/sandbox/:id` (and compatible payloads). */
export type SandboxFullReportLinks = {
    pdf?: string;
    html?: string;
    json?: string;
};

export const getSandboxFullReportExportOptions = (
    fullReport: SandboxFullReportLinks | null | undefined,
    t: (key: string) => string,
): SandboxFullReportExportOption[] => {
    if (!fullReport) {
        return [];
    }

    const options: SandboxFullReportExportOption[] = [];

    if (fullReport.pdf) {
        options.push({ key: 'pdf', label: t('PDF'), icon: 'report', url: fullReport.pdf });
    }

    if (fullReport.html) {
        options.push({ key: 'html', label: t('HTML'), icon: 'checklist', url: fullReport.html });
    }

    if (fullReport.json) {
        options.push({ key: 'json', label: t('JSON'), icon: 'base', url: fullReport.json });
    }

    return options;
};

export const openSandboxFullReportDownload = (url?: string): void => {
    if (!url) {
        return;
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export const getSandboxReportDownloadHoverHint = (t: (key: string) => string): string =>
    t('In order to download the Sandbox Report, please enable Sandbox Analysis');
